import { useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '../../api';
import type { MsrAnlageZeile, MsrVdiFunktionsliste } from './typen';
import { z } from './gemeinsam';
import { FunktionslisteVdiTabelle } from './FunktionslisteVdiTabelle';
import { PdfKnopf } from './PdfKnopf';
import { EmptyState, ErrorState, Feld, LoadingState } from '../../muster';

/**
 * Ansicht „VDI 3814" des Reiters Datenpunktliste — links Suchfeld und Liste
 * der Anlagen (schon nach dem Filter der flachen Ansicht eingegrenzt,
 * vorbelegt mit der ersten Anlage; diese Ansicht ist der Standard-Einstieg
 * hier, siehe `Datenpunkte.tsx`), rechts die GA-Funktionsliste VDI 3814
 * Blatt 4.3 dieser Anlage (Tabelle in `FunktionslisteVdiTabelle`, dieselbe
 * wie im Reiter Schemen). Excel-Knöpfe für die Anlagendatei und die
 * ASP-Sammeldatei (Vorgabe Teuber + Viel, Abschnitt 13.5).
 */
export function DatenpunktlisteVdi({ api, projektId, anlagen }: {
  api: ApiClient;
  projektId: string;
  anlagen: MsrAnlageZeile[];
}) {
  const [suche, setSuche] = useState('');
  const [anlageId, setAnlageId] = useState('');
  const [funktionsliste, setFunktionsliste] = useState<MsrVdiFunktionsliste | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [excelAnlageLaeuft, setExcelAnlageLaeuft] = useState(false);
  const [excelAspLaeuft, setExcelAspLaeuft] = useState(false);

  // Vorbelegung mit der ersten Anlage des aktuellen Filters — greift auch,
  // wenn die bisherige Auswahl aus der gefilterten Liste herausfällt.
  useEffect(() => {
    if (!anlagen.some((a) => a.id === anlageId)) {
      setAnlageId(anlagen[0]?.id ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anlagen]);

  useEffect(() => {
    if (!anlageId) { setFunktionsliste(null); return; }

    setLaedt(true);
    setFehler(null);
    api.msrVdiFunktionsliste(projektId, anlageId)
      .then((f) => { setFunktionsliste(f); setLaedt(false); })
      .catch((e) => { setFehler(e instanceof Error ? e.message : String(e)); setLaedt(false); });
  }, [api, projektId, anlageId]);

  const gefiltert = useMemo(() => {
    const s = suche.trim().toLowerCase();
    if (!s) return anlagen;
    return anlagen.filter((a) => a.kennung.toLowerCase().includes(s)
      || (a.bezeichnung ?? '').toLowerCase().includes(s));
  }, [anlagen, suche]);

  const gewaehlteAnlage = anlagen.find((a) => a.id === anlageId);

  const excelAnlage = async () => {
    if (!anlageId || !funktionsliste) return;

    setExcelAnlageLaeuft(true);
    try {
      await api.msrVdiFunktionslisteExcel(
        projektId, `VDI-DP-Liste_${funktionsliste.kopf.anlage}.xlsx`,
        { anlage: anlageId });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setExcelAnlageLaeuft(false);
    }
  };

  const excelAsp = async () => {
    if (!gewaehlteAnlage?.aspId) return;

    setExcelAspLaeuft(true);
    try {
      await api.msrVdiFunktionslisteExcel(
        projektId, `VDI-DP-Liste_${gewaehlteAnlage.aspKennung ?? gewaehlteAnlage.aspId}.xlsx`,
        { asp: gewaehlteAnlage.aspId });
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setExcelAspLaeuft(false);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div className="baum" style={{ minWidth: 260, maxWidth: 320 }}>
        <div style={{ padding: 8, borderBottom: '1px solid var(--linie)' }}>
          <Feld art="suche" beschriftung="Anlage suchen" wert={suche} platzhalter="Kennung oder Bezeichnung …"
            beiAenderung={(w) => setSuche((w as string) ?? '')} />
        </div>

        <div style={{ maxHeight: 480, overflowY: 'auto' }}>
          {gefiltert.map((a) => (
            <button key={a.id} type="button"
              className={`baum-zeile${anlageId === a.id ? ' aktiv' : ''}`}
              onClick={() => setAnlageId(a.id)}>
              <span>{a.kennung}</span>
              {a.bezeichnung && (
                <span className="schwach" style={{ fontSize: 11 }}>{a.bezeichnung}</span>
              )}
            </button>
          ))}
          {gefiltert.length === 0 && (
            <EmptyState klein titel="Keine Anlage gefunden."
              erklaerung={anlagen.length === 0
                ? 'Der Filter der flachen Ansicht (ASP/Gewerk) lässt keine Anlage übrig.'
                : 'Keine Kennung und keine Bezeichnung passt zur Suche.'} />
          )}
        </div>
      </div>

      <div style={{ flex: '1 1 560px', minWidth: 0 }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          gap: 8, marginBottom: 8, flexWrap: 'wrap',
        }}>
          <span className="schwach" style={{ fontSize: 12 }}>
            {funktionsliste
              ? `${z(funktionsliste.zeilen.length)} Zeilen, ${z(funktionsliste.seiten)} Blätter`
              : ''}
          </span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" className="knopf" disabled={!funktionsliste || excelAnlageLaeuft}
              onClick={excelAnlage}>
              Excel (Anlage)
            </button>
            <button type="button" className="knopf"
              disabled={!gewaehlteAnlage?.aspId || excelAspLaeuft}
              onClick={excelAsp}>
              Excel (ASP-Sammeldatei)
            </button>
            {anlageId && (
              <PdfKnopf api={api} projektId={projektId} art="dpliste" ziel="anlage" zielId={anlageId}
                label="Datenpunktliste" />
            )}
          </div>
        </div>

        {fehler && (
          <ErrorState klein meldung="Die GA-Funktionsliste konnte nicht geladen oder ausgegeben werden."
            technisch={fehler} />
        )}
        {laedt && <LoadingState text="GA-Funktionsliste abrufen …" zeilen={8} spalten={5} />}

        {!laedt && !fehler && !funktionsliste && (
          <EmptyState titel="Keine Anlage gewählt."
            erklaerung="Links eine Anlage wählen - rechts erscheint ihre GA-Funktionsliste nach VDI 3814 Blatt 4.3." />
        )}

        {!laedt && funktionsliste && <FunktionslisteVdiTabelle f={funktionsliste} />}
      </div>
    </div>
  );
}
